import { Route, Switch, useLocation } from "react-router-dom";
import React, { useContext, useEffect, useState } from "react";
import { PAGES } from "./routing/pages";
import PrivateLayout from "./navigation/layout/PrivateLayout";
import NotFoundContainer from "./screens/not-found/container";
import { useDispatch } from "react-redux";
import { History } from "history";
import firebase from "./firebase/firebase";
import { Page } from "./data/interfaces";
import { IModalContext, ModalContext } from "./providers/Modal";
import { FUNCTIONS } from "./firebase/functions";
import Axios from "axios";
import { useTranslation } from "react-i18next";
import _environment from "./_environment/_environment";
import Button from "./components/shared/button/Button";
import { addBreadcrumb } from "./context/breadcrumbs/action";
import Cookies from "js-cookie";
import { COOKIES } from "./data/enums";
import CookieBar from "./components/shared/cookieBar/CookieBar";

declare const $: any;

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {
  history: History;
}

const Main = ({ history, ...restProps }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  // Reference to Redux store dispatch function: use it to dispatch actions.
  const dispatch = useDispatch();
  let location = useLocation();

  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // Access to modal manager hook that you may use to open and close modals.
  const { openModal, closeModal } = useContext<IModalContext>(ModalContext);

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------
  const [showCookieBar, setShowCookieBar] = useState<boolean>(false);

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  const setupDevMode = async () => {
    firebase.functions().useFunctionsEmulator("http://localhost:5001");

    try {
      // Try check cloud function status
      await Axios.get(`${_environment.firebase.functionsURL}/${FUNCTIONS.SERVER_STATUS}`);
    } catch (err) {
      console.warn(
        "Development mode enabled, but cloud functions emulator is not running. Please run firebase emulators:start --only=functions"
      );
    }
  };

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * Check for connection status every x seconds
   * @function
   */
  const checkConnectionStatus = () => {
    const seconds = 5;
    let connectionModalOpened = false;

    setInterval(() => {
      console.log({ statusOnline: navigator.onLine, connectionModalOpened });

      if (!navigator.onLine && !connectionModalOpened) {
        console.log("open modal");
        connectionModalOpened = true;
        openModal({
          size: "sm",
          customContent: (
            <>
              <img className="img-responsive" src="/assets/images/undraw/server_down.svg" />

              <h2 className="mt-4">{t("offlinePopup.title")}</h2>

              <p className="mb-4">{t("offlinePopup.description")}</p>
            </>
          ),
          closable: false,
        });
      } else if (!!navigator.onLine && !!connectionModalOpened) {
        closeModal();
        connectionModalOpened = false;
      }
    }, 1000 * seconds);
  };

  /**
   * Handles back to top button visibility
   * @function
   */
  const handleScrollTop = () => {
    const button = $("#back-to-top");

    if (!!button && !!button.length) {
      const _scroll = (scroll: number) => {
        if (scroll > 250) {
          button.addClass("visible");
        } else {
          button.removeClass("visible");
        }
      };

      _scroll($("body").scrollTop());

      $("body").scroll((event: any) => {
        _scroll($("body").scrollTop());
      });
    }

    // On click, back to top
    button.on("click", () => {
      $("html,body").animate({ scrollTop: 0 }, 600);
    });
  };

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------
  const renderRouteContent = (page: Page, props: any): React.ReactNode => {
    return (
      <page.layout history={props.history} key={page.key} alwaysVisible={page.alwaysVisible}>
        <page.component props />
      </page.layout>
    );
  };

  const renderRoute = (page: Page): React.ReactNode => {
    return (
      <Route
        key={page.key}
        exact={true}
        path={page.path}
        render={(props) => {
          return renderRouteContent(page, props);
        }}
      />
    );
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    // init component
    console.log("[Main] init");

    if (process.env.NODE_ENV === "development") {
      setupDevMode();
    }

    // Listen to firebase changes
    firebase.auth().onAuthStateChanged(async (authUser:any) => {
      if (!authUser) {
        await firebase.auth().signOut();

        dispatch({
          type: "USER_UPDATE",
          payload: null,
        });
      }
    });

    checkConnectionStatus();
    handleScrollTop();

    if (!Cookies.get(COOKIES.COOKIES_ACCEPTED)) {
      setShowCookieBar(true);
    }
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    // destroy component
    console.log("[Main] destroy");
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  React.useEffect(() => {
    dispatch(addBreadcrumb(location));
  }, [location.pathname]);

  return (
    <>
      <Switch>
        {PAGES.map((page, index) => (
          <Route
            key={page.key}
            exact={page.exact}
            path={page.path}
            render={(props) => {
              return page.nested ? (
                <Switch>
                  {renderRoute({ ...page, ...{ exact: true } })}
                  {page.nested.map((item: Page) => renderRoute(item))}
                </Switch>
              ) : (
                renderRouteContent(page, props)
              );
            }}
          />
        ))}
        <Route
          render={(props) => (
            <PrivateLayout history={props.history}>
              <NotFoundContainer {...props} />
            </PrivateLayout>
          )}
        />
      </Switch>

      {/* Scroll to Top */}
      <Button className="btn btn-circle btn-primary btn-icon" id="back-to-top">
        <i className="fas fa-angle-up"></i>
      </Button>

      {/* Cookie bar */}
      {!!showCookieBar && <CookieBar onClose={() => setShowCookieBar(false)} />}
    </>
  );
};

export default Main;
